import { between, betweenRun, DECENT_GAP, doseWindow, EVENING_MINUTES, GAP_CLOSEABLE_STEPS, gapAhead, glp1Reports, LATE_BREAK_MINUTES, noBodyRead, none, offUsual, PREP_MINUTES, PUSHED_HARD_SHARE, REENGAGE_QUIET_DAYS, SHORT_NIGHT_MARGIN_HOURS, STREAK_SAVE_SHORTFALL_STEPS, saidDepleted, seated, sustained, trains, WORKOUT_SLOT_UNTIL, } from "./arm-reads.js";
import { clock, hoursMinutes, span } from "./clock.js";
import { PROTECT_HOLD_MINUTES } from "./limits.js";
/**
 * The playbook: every play Heartbeat can offer, one entry each. This file
 * holds plays and nothing else, so a diff here is always a change to what
 * members are offered. Each entry is the outcome contract (pillar, metric,
 * window, target, confirmation copy), the cue (`when`), the template copy,
 * and where a tap goes.
 *
 * Shared thresholds and reads are in arm-reads.ts; the shape is in
 * arm-types.ts; the lookups and fingerprint are in arms.ts. A new play also
 * needs its rule id in COIN_PLAYS in the heartbeat-tunables manifest, and
 * arms-contract.test.ts fails CI on an incomplete contract.
 */
export const ARMS = [
    {
        capability: "restructure",
        tier: "important",
        group: "workday",
        watches: "load",
        workScope: "now",
        rung: 3,
        action: "rule.restructure_time",
        pillar: "stress",
        metric: "block_moved",
        windowMinutes: 120,
        target: null,
        confirmed: "That meeting moved",
        eyebrow: "Only if you want to",
        requiresConfirmation: true,
        handoff: "calendar",
        // Depletion is a point read and a run is a trajectory:
        when: (r) => r.eligible &&
            r.heavy &&
            (r.depletion >= 0.7 || sustained(r) || noBodyRead(r)) &&
            (r.blocks === undefined || r.moveTarget !== null),
        template: {
            title: "Want to make a little room in your day?",
            body: "Today is packed, and your readings suggest you may be running low. Moving one meeting would give you some breathing room.",
            primaryLabel: "Open calendar",
            calendarSlot: { startMinutes: 16 * 60, durationMinutes: 30 },
            draftMessage: "Hey team, going to push our 4pm to Thursday, nothing urgent on my side. Same time work?",
            triggeredBy: "calendar.load",
        },
    },
    {
        capability: "protect",
        tier: "timely",
        group: "workday",
        watches: "load",
        workScope: "now",
        rung: 2,
        action: "rule.protect_time",
        pillar: "stress",
        metric: "block_kept",
        windowMinutes: 120,
        target: null,
        confirmed: "You kept that time free",
        eyebrow: "Add to your calendar",
        requiresConfirmation: false,
        handoff: "calendar",
        when: (r) => r.eligible &&
            (r.depletion >= 0.5 || noBodyRead(r)) &&
            r.runAhead >= 3 &&
            (r.blocks === undefined || r.protectSlot !== null),
        template: {
            title: "Want some breathing room before the meetings?",
            body: "You have several meetings in a row. Saving a few minutes beforehand gives you time to reset.",
            primaryLabel: "Open calendar",
            calendarSlot: {
                startMinutes: 15 * 60 - PROTECT_HOLD_MINUTES,
                durationMinutes: PROTECT_HOLD_MINUTES,
            },
            draftMessage: null,
            triggeredBy: "calendar.event",
        },
    },
    {
        capability: "back_to_back_prep",
        tier: "important",
        group: "workday",
        watches: "sitting",
        workScope: "now",
        rung: 1,
        action: "rule.back_to_back_prep",
        pillar: "movement",
        metric: "desk_gap",
        windowMinutes: 30,
        target: null,
        confirmed: "You got up before it started",
        eyebrow: "Before they start",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => seated(r) &&
            r.run !== null &&
            r.run.length >= 3 &&
            r.run.start.startMinutes > r.nowMinutes &&
            r.run.start.startMinutes - r.nowMinutes <= PREP_MINUTES,
        evidence: (r) => [
            { label: "Meetings in a row", value: String(r.run?.length ?? 0), kind: "calendar.event" },
            {
                label: "First starts",
                value: clock(r.run?.start.startMinutes ?? r.nowMinutes),
                kind: "calendar.event",
            },
        ],
        template: {
            title: "Time for a quick breather?",
            body: "Several meetings are about to start back to back. A glass of water and a quick stretch may make the next few hours easier.",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "calendar.event",
        },
    },
    {
        capability: "between_meetings",
        tier: "timely",
        group: "workday",
        watches: "sitting",
        workScope: "now",
        rung: 1,
        action: "rule.between_meetings",
        pillar: "movement",
        metric: "desk_gap",
        windowMinutes: 30,
        target: null,
        confirmed: "You used the gap",
        eyebrow: "A few minutes",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => seated(r) && betweenRun(r) !== null,
        evidence: (r) => [
            {
                label: "Next meeting",
                value: clock(betweenRun(r)?.endsAt ?? r.nowMinutes),
                kind: "calendar.event",
            },
        ],
        template: {
            title: "A few free minutes between meetings",
            body: "Your next meeting has not started yet. Feel like standing up or stretching?",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "calendar.event",
        },
    },
    {
        capability: "recovery",
        tier: "important",
        group: "workday",
        watches: "overnight",
        rung: 1,
        action: "rule.inject_recovery",
        pillar: "stress",
        metric: "acted",
        windowMinutes: 30,
        target: null,
        confirmed: "You took that minute",
        eyebrow: "Right now",
        requiresConfirmation: false,
        handoff: "reset",
        // Any overnight reading off this member's own usual, not resting rate alone:
        when: (r) => r.eligible &&
            (r.body.restingHeartRateElevated ||
                offUsual(r).length > 0 ||
                r.stress.acute ||
                saidDepleted(r)) &&
            r.openNow,
        template: {
            title: "Want a quiet minute before the next thing?",
            body: "Your overnight readings were outside your usual range. A slow breathing reset might help you feel more settled.",
            primaryLabel: "Open a reset",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "wearable.rhr",
        },
    },
    {
        capability: "heavy_tomorrow",
        tier: "routine",
        group: "workday",
        watches: "load",
        workScope: "tomorrow",
        rung: 1,
        action: "rule.heavy_tomorrow",
        pillar: "sleep",
        metric: "sleep_onset",
        windowMinutes: 720,
        offerMinutes: 120,
        target: 240,
        confirmed: "You got ahead of tomorrow",
        eyebrow: "Tomorrow",
        requiresConfirmation: false,
        handoff: "reset",
        // Read, not inferred: the week is already synced, so the one honest
        // prediction the engine can make is the one it can look up.
        when: (r) => r.nowMinutes >= EVENING_MINUTES &&
            r.tomorrow !== null &&
            r.tomorrow.meetingMinutes >= r.heavyThreshold &&
            r.tomorrow.meetingMinutes > r.forecast.meetingMinutes,
        evidence: (r) => [
            {
                label: "Tomorrow",
                value: `${span(r.tomorrow?.meetingMinutes ?? 0)} across ${r.tomorrow?.blockCount ?? 0}`,
                kind: "calendar.load",
            },
            { label: "Today", value: span(r.forecast.meetingMinutes), kind: "calendar.load" },
        ],
        template: {
            title: "An easier start tomorrow?",
            body: "Tomorrow is busier than today. Keeping to your usual bedtime may make the morning feel easier.",
            primaryLabel: "Open a reset",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "calendar.load",
        },
    },
    {
        capability: "wind_down",
        tier: "timely",
        group: "workday",
        watches: "overnight",
        rung: 1,
        action: "rule.wind_down",
        pillar: "stress",
        metric: "sleep_onset",
        windowMinutes: 720,
        offerMinutes: 90,
        target: 240,
        confirmed: "You wound down",
        eyebrow: "This evening",
        requiresConfirmation: false,
        handoff: "reset",
        when: (r) => r.eligible && r.stress.windDown,
        template: {
            title: "A little more time to wind down",
            body: "Today has been busy. Starting your wind-down a little earlier could help you sleep tonight.",
            primaryLabel: "Open a reset",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "wearable.stress",
        },
    },
    {
        /** A big day on a short night. */
        capability: "pushed_on_short_sleep",
        tier: "routine",
        group: "workday",
        watches: "overnight",
        rung: 1,
        action: "rule.pushed_on_short_sleep",
        pillar: "sleep",
        metric: "sleep_onset",
        windowMinutes: 720,
        offerMinutes: 120,
        target: 240,
        confirmed: "You got to bed at your usual time",
        eyebrow: "Tonight",
        requiresConfirmation: false,
        handoff: "reset",
        when: (r) => r.nowMinutes >= EVENING_MINUTES &&
            r.body.sleepHours != null &&
            r.body.sleepHours < r.limits.sleepTargetHours - SHORT_NIGHT_MARGIN_HOURS &&
            r.body.stepsToday != null &&
            r.body.usualSteps != null &&
            r.body.stepsToday > r.body.usualSteps * PUSHED_HARD_SHARE,
        evidence: (r) => [
            { label: "Last night", value: hoursMinutes(r.body.sleepHours), kind: "wearable.sleep" },
            {
                label: "Steps today",
                value: none(r.body.stepsToday),
                kind: "wearable.activity",
            },
            {
                label: "Your usual",
                value: none(r.body.usualSteps == null ? null : Math.round(r.body.usualSteps)),
                kind: "wearable.activity",
            },
        ],
        template: {
            title: "An earlier night might feel good",
            body: "You slept less than usual and moved more today. Your usual bedtime could give you more room to rest.",
            primaryLabel: "Open a reset",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "wearable.sleep",
        },
    },
    {
        capability: "idle",
        tier: "timely",
        group: "workday",
        refractoryHours: 1,
        watches: "sitting",
        rung: 1,
        action: "rule.break_idle",
        pillar: "movement",
        metric: "desk_gap",
        windowMinutes: 30,
        target: null,
        confirmed: "You got moving",
        eyebrow: "You have not moved",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => r.eligible && r.idle && r.openNow && r.body.movementState === "still",
        template: {
            title: "Feel like getting on your feet?",
            body: "You have been still for a while. A few minutes of movement might help you feel less stiff.",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "wearable.activity",
        },
    },
    {
        capability: "weather_window",
        listenable: true,
        tier: "timely",
        group: "workday",
        watches: "outdoors",
        rung: 1,
        action: "rule.weather_window",
        pillar: "movement",
        metric: "steps",
        windowMinutes: 60,
        target: 800,
        confirmed: "That walk counted",
        eyebrow: "Rain clearing",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => r.eligible && r.clearing && !r.enoughToday,
        template: {
            title: "A dry spell is on the way",
            body: "There is a dry spell coming up. Feel like getting out for a quick walk?",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "environment.weather_today",
        },
    },
    {
        capability: "walk",
        listenable: true,
        tier: "timely",
        group: "workday",
        watches: "steps",
        rung: 1,
        action: "rule.take_walk",
        pillar: "movement",
        metric: "steps",
        windowMinutes: 30,
        target: 800,
        confirmed: "That walk counted",
        eyebrow: "A walk fits",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => r.eligible &&
            r.lowMidday &&
            r.openNow &&
            r.body.movementState !== "moving" &&
            r.body.workoutToday == null,
        template: {
            title: "Feel like taking a quick walk?",
            body: "Your step count is lower than usual today. A short walk might be a good reset.",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "wearable.activity",
        },
    },
    {
        capability: "daylight_window",
        listenable: true,
        tier: "timely",
        group: "workday",
        watches: "outdoors",
        rung: 1,
        action: "rule.daylight_window",
        pillar: "movement",
        metric: "steps",
        windowMinutes: 60,
        target: 800,
        confirmed: "You caught the light",
        eyebrow: "Daylight left",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => r.eligible && r.lastLight && r.openNow && !r.enoughToday && r.body.walkedToday !== true,
        template: {
            title: "There's still time to get outside before sunset",
            body: "If some fresh air sounds good, a short walk would make the most of the daylight.",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "environment.weather_today",
        },
    },
    {
        capability: "next_break_late",
        tier: "timely",
        group: "workday",
        watches: "sitting",
        workScope: "now",
        rung: 1,
        action: "rule.next_break_late",
        pillar: "movement",
        metric: "desk_gap",
        windowMinutes: 30,
        target: null,
        confirmed: "You took the break",
        eyebrow: "Long stretch ahead",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => {
            if (!seated(r) || r.run === null || r.run.length < 2)
                return false;
            const startsIn = r.run.start.startMinutes - r.nowMinutes;
            const runStart = r.run.start.startMinutes;
            const breakAfter = r.forecast.gaps.find((g) => g.startMinutes > runStart);
            return (startsIn > 0 &&
                startsIn <= 30 &&
                (breakAfter === undefined || breakAfter.startMinutes - r.nowMinutes >= LATE_BREAK_MINUTES));
        },
        evidence: (r) => [
            {
                label: "Next proper break",
                value: (() => {
                    const runStart = r.run?.start.startMinutes ?? r.nowMinutes;
                    const g = r.forecast.gaps.find((x) => x.startMinutes > runStart);
                    return g ? clock(g.startMinutes) : "Not before the end of the day";
                })(),
                kind: "calendar.gap",
            },
        ],
        template: {
            title: "A quick pause now?",
            body: "You have a long run of meetings ahead. A drink and a stretch now may make it easier.",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "calendar.load",
        },
    },
    {
        capability: "decent_gap",
        listenable: true,
        tier: "timely",
        group: "workday",
        watches: "sitting",
        workScope: "now",
        rung: 1,
        action: "rule.decent_gap",
        pillar: "movement",
        metric: "desk_gap",
        windowMinutes: 30,
        target: null,
        confirmed: "You used the gap",
        eyebrow: "A gap ahead",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => {
            const gap = gapAhead(r);
            return (seated(r) &&
                gap !== null &&
                gap.durationMinutes >= DECENT_GAP.min &&
                gap.durationMinutes <= DECENT_GAP.max);
        },
        evidence: (r) => [
            {
                label: "Free until",
                value: clock(gapAhead(r)?.endMinutes ?? r.nowMinutes),
                kind: "calendar.gap",
            },
        ],
        template: {
            title: "A little time to yourself",
            body: "You have a gap before the next meeting. Feel like a walk or a quick reset?",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "calendar.gap",
        },
    },
    {
        capability: "workout_slot",
        tier: "routine",
        group: "fallback",
        watches: "steps",
        rung: 1,
        action: "rule.workout_slot",
        pillar: "movement",
        metric: "workout",
        windowMinutes: 480,
        offerMinutes: 240,
        target: null,
        confirmed: "Workout in",
        eyebrow: "Nothing in yet",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => trains(r) &&
            r.body.stepsToday != null &&
            r.body.stepsToday < r.middayFloor &&
            r.body.workoutToday == null &&
            r.body.walkedToday !== true &&
            r.nowMinutes < WORKOUT_SLOT_UNTIL &&
            (r.openNow || (r.nextGap !== null && r.nextGap.durationMinutes >= 60)),
        evidence: (r) => [
            { label: "Workouts today", value: "None yet", kind: "wearable.workout" },
            ...(r.nextGap
                ? [
                    {
                        label: "Free",
                        value: `${clock(r.nextGap.startMinutes)} to ${clock(r.nextGap.endMinutes)}`,
                        kind: "calendar.gap",
                    },
                ]
                : [{ label: "Free", value: "The rest of the day", kind: "calendar.gap" }]),
        ],
        template: {
            title: "Want to make time for a workout?",
            body: "You usually train, and there is room in your day. This looks like a good window.",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "wearable.workout",
        },
    },
    {
        capability: "workout_evening",
        tier: "timely",
        group: "fallback",
        watches: "steps",
        rung: 1,
        action: "rule.workout_evening",
        pillar: "movement",
        metric: "workout",
        windowMinutes: 90,
        target: null,
        confirmed: "Workout in",
        eyebrow: "Still time",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => trains(r) &&
            r.nowMinutes >= EVENING_MINUTES &&
            !r.enoughToday &&
            r.body.stepsToday != null &&
            r.body.workoutToday == null &&
            r.body.walkedToday !== true &&
            r.openNow,
        evidence: (r) => [
            { label: "Workouts today", value: "None yet", kind: "wearable.workout" },
            { label: "Steps today", value: none(r.body.stepsToday), kind: "wearable.activity" },
        ],
        template: {
            title: "Still feel like moving today?",
            body: "Nothing is logged yet, and your evening is open. A short session is still an option.",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "wearable.workout",
        },
    },
    {
        capability: "streak_freeze",
        tier: "timely",
        group: "connection",
        watches: "overnight",
        rung: 1,
        action: "rule.streak_freeze",
        pillar: "connection",
        metric: "acted",
        windowMinutes: 30,
        target: null,
        confirmed: "Your streak is held",
        eyebrow: "Protect your streak",
        requiresConfirmation: false,
        handoff: "connection",
        // Only once the day is genuinely spent.
        when: (r) => r.atRisk && r.sleepDebt && r.nowMinutes >= EVENING_MINUTES,
        template: {
            title: "Want to give yourself the evening off?",
            body: "You slept less than usual. A freeze protects your streak without asking more from you today.",
            primaryLabel: "Use a freeze",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "wearable.sleep",
        },
    },
    {
        capability: "streak_save",
        listenable: true,
        tier: "important",
        group: "connection",
        watches: "steps",
        rung: 1,
        action: "rule.streak_save",
        pillar: "connection",
        metric: "steps",
        windowMinutes: 60,
        target: 800,
        confirmed: "Your streak is safe",
        eyebrow: "Your streak is on the line",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => r.atRisk &&
            !r.sleepDebt &&
            r.shortfall !== null &&
            r.shortfall <= STREAK_SAVE_SHORTFALL_STEPS,
        template: {
            title: "Want to keep your streak going?",
            body: "You are close to today's goal. A short walk would get you there.",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "wearable.activity",
        },
    },
    {
        capability: "close_the_gap",
        listenable: true,
        tier: "routine",
        group: "connection",
        watches: "steps",
        rung: 1,
        action: "rule.close_the_gap",
        pillar: "connection",
        metric: "steps",
        // Most members get this mid workday and walk when a meeting ends, not
        // when the push lands. The refractory covers the window so two offers
        // never score the same steps.
        windowMinutes: 480,
        offerMinutes: 120,
        refractoryHours: 8,
        target: 500,
        confirmed: "You held your place",
        eyebrow: "Within reach",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => !r.sleepDebt &&
            r.outdoorOk !== false &&
            r.gapToNextRank !== null &&
            r.gapToNextRank > 0 &&
            r.gapToNextRank <= GAP_CLOSEABLE_STEPS,
        template: {
            title: "The next rank is close",
            body: "You are close to moving up. Feel like going for it with a short walk?",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "challenge.standing",
        },
    },
    {
        capability: "close_the_gap_indoor",
        tier: "timely",
        group: "connection",
        watches: "steps",
        rung: 1,
        action: "rule.close_the_gap_indoor",
        pillar: "connection",
        metric: "steps",
        // Most members get this mid workday and walk when a meeting ends, not
        // when the push lands. The refractory covers the window so two offers
        // never score the same steps.
        windowMinutes: 480,
        offerMinutes: 120,
        refractoryHours: 8,
        target: 500,
        confirmed: "You closed it indoors",
        eyebrow: "Indoors works",
        requiresConfirmation: false,
        handoff: "movement",
        when: (r) => !r.sleepDebt &&
            r.outdoorOk === false &&
            r.gapToNextRank !== null &&
            r.gapToNextRank > 0 &&
            r.gapToNextRank <= GAP_CLOSEABLE_STEPS,
        evidence: (_r, c) => [
            {
                label: "Behind the next rank",
                value: none(c?.standing?.gapToNextRank),
                kind: "challenge.standing",
            },
            { label: "Outside", value: "Not right now", kind: "environment.weather_today" },
        ],
        template: {
            title: "The next rank is close",
            body: "The weather is not ideal, but indoor movement counts too. Feel like a lap of the stairs or corridor?",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "challenge.standing",
        },
    },
    {
        capability: "challenge_starting",
        tier: "timely",
        group: "connection",
        rung: 1,
        action: "rule.challenge_starting",
        pillar: "connection",
        metric: "challenge_joined",
        windowMinutes: 60,
        target: 1,
        confirmed: "You are in",
        eyebrow: "Starting today",
        requiresConfirmation: false,
        handoff: "connection",
        // "Starting" lasts 20 hours. Under the fleet's 3h refractory one launch
        // could be announced to the same member four or more times.
        refractoryHours: 24,
        leadsLadder: true,
        when: (r) => !r.sleepDebt && r.challengeStarting,
        template: {
            title: "A challenge starts today",
            body: "Want to join from day one?",
            primaryLabel: "Join now",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "challenge.starting",
        },
    },
    {
        capability: "challenge_underway",
        tier: "timely",
        group: "connection",
        rung: 1,
        action: "rule.challenge_underway",
        pillar: "connection",
        metric: "challenge_joined",
        windowMinutes: 60,
        target: 1,
        confirmed: "You are in",
        eyebrow: "Underway",
        requiresConfirmation: false,
        handoff: "connection",
        // The challenge runs for weeks and the member has not said yes yet, so
        // the ask comes back every few days rather than every day.
        refractoryHours: 72,
        leadsLadder: true,
        when: (r) => !r.sleepDebt && r.challengeUnderway,
        template: {
            title: "Your teammates are walking",
            body: "The company challenge is underway. Want to get started?",
            primaryLabel: "Join now",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "challenge.underway",
        },
    },
    {
        capability: "being_caught",
        tier: "routine",
        group: "connection",
        watches: "steps",
        rung: 1,
        action: "rule.being_caught",
        pillar: "connection",
        metric: "steps",
        // Most members get this mid workday and walk when a meeting ends, not
        // when the push lands. The refractory covers the window so two offers
        // never score the same steps.
        windowMinutes: 480,
        offerMinutes: 120,
        refractoryHours: 8,
        target: 500,
        confirmed: "You held your place",
        eyebrow: "Someone is close",
        requiresConfirmation: false,
        handoff: "connection",
        when: (r) => !r.sleepDebt && r.gapBehind !== null && r.gapBehind <= GAP_CLOSEABLE_STEPS,
        evidence: (_r, c) => [
            {
                label: "Lead over the rank below",
                value: c?.standing?.gapBehind === 0 ? "Level" : none(c?.standing?.gapBehind),
                kind: "challenge.standing",
            },
        ],
        template: {
            title: "Someone is closing the gap",
            body: "A short walk would help you hold your place. Want to go for it?",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "challenge.standing",
        },
    },
    {
        capability: "group_route_complete",
        tier: "timely",
        group: "connection",
        rung: 1,
        action: "rule.group_route_complete",
        pillar: "connection",
        metric: "app_opened",
        windowMinutes: 240,
        target: 1,
        confirmed: "You saw the finish",
        eyebrow: "Challenge complete",
        requiresConfirmation: false,
        handoff: "connection",
        // One announcement per finish. The state read looks back 24 hours, so the
        // refractory has to cover the whole window or a later tick repeats the news.
        refractoryHours: 24,
        when: (r) => r.groupRouteComplete,
        template: {
            title: "Your group finished the route",
            body: "The whole route is complete. Every member's ordinary days of movement added up to it.",
            primaryLabel: "See the finish",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "challenge.milestone",
        },
    },
    {
        capability: "teammate_win",
        tier: "routine",
        group: "connection",
        rung: 1,
        action: "rule.teammate_win",
        pillar: "connection",
        metric: "kudos_sent",
        windowMinutes: 60,
        target: 1,
        confirmed: "Your cheer is sent",
        eyebrow: "Your teammate finished",
        requiresConfirmation: false,
        handoff: "connection",
        // The read looks back a day at the same finish, so the fleet's 3h
        // refractory let one teammate's win push four or five times.
        refractoryHours: 24,
        when: (r) => r.teammateWin,
        template: {
            title: "Your teammate finished their route",
            body: "Want to send them a cheer?",
            primaryLabel: "Send a cheer",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "challenge.milestone",
        },
    },
    {
        capability: "glp1_small_meal",
        tier: "timely",
        group: "fallback",
        refractoryHours: 20,
        rung: 1,
        action: "rule.glp1_small_meal",
        pillar: "food",
        metric: "acted",
        windowMinutes: 120,
        target: null,
        confirmed: "You ate something",
        eyebrow: "Around your dose",
        requiresConfirmation: false,
        handoff: "done",
        when: (r) => r.openNow &&
            between(r, 11 * 60, 14 * 60) &&
            glp1Reports(r, "low_appetite") &&
            (r.body.glp1?.daysSinceDose ?? 9) <= 2,
        template: {
            title: "Would a small lunch feel manageable?",
            body: "You said your appetite drops after a dose. A few bites with some protein still count.",
            primaryLabel: "I did it",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "self_report.glp1_dose",
        },
    },
    {
        capability: "glp1_sip_water",
        tier: "timely",
        group: "fallback",
        refractoryHours: 20,
        rung: 1,
        action: "rule.glp1_sip_water",
        pillar: "food",
        metric: "acted",
        windowMinutes: 120,
        target: null,
        confirmed: "You had a drink",
        eyebrow: "Dose day",
        requiresConfirmation: false,
        handoff: "done",
        when: (r) => r.openNow && between(r, 13 * 60, 17 * 60) && glp1Reports(r, "nausea") && doseWindow(r),
        template: {
            title: "Would a few small sips feel easier?",
            body: "You said nausea is worse around your dose. Small sips may feel better than a full glass.",
            primaryLabel: "I did it",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "self_report.glp1_dose",
        },
    },
    {
        capability: "glp1_strength",
        tier: "routine",
        group: "fallback",
        refractoryHours: 44,
        rung: 1,
        action: "rule.glp1_strength",
        pillar: "movement",
        metric: "workout",
        windowMinutes: 180,
        target: null,
        confirmed: "Strength work done",
        eyebrow: "Keep your muscle",
        requiresConfirmation: false,
        handoff: "done",
        when: (r) => r.openNow &&
            between(r, 16 * 60, 19 * 60) &&
            r.body.glp1 != null &&
            !doseWindow(r) &&
            r.body.workoutToday == null,
        template: {
            title: "Feel like a short strength session?",
            body: "A short strength session can help support muscle while you're taking a GLP-1. Ten minutes is enough for today.",
            primaryLabel: "Will do",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "self_report.glp1_dose",
        },
    },
    {
        capability: "reengage",
        tier: "routine",
        group: "last_resort",
        rung: 1,
        action: "rule.reengage",
        pillar: "connection",
        metric: "acted",
        windowMinutes: 240,
        target: null,
        confirmed: "Good to see you",
        eyebrow: "It has been a few days",
        requiresConfirmation: false,
        handoff: "connection",
        when: (r) => r.quietDays != null && r.quietDays >= REENGAGE_QUIET_DAYS,
        template: {
            title: "A few updates from your team",
            body: "HealthOS has been quiet for a few days. Want to catch up?",
            primaryLabel: "Open community",
            calendarSlot: null,
            draftMessage: null,
            triggeredBy: "user.app_open",
        },
    },
];
//# sourceMappingURL=playbook.js.map